import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Award,
  Bot,
  Building2,
  Calendar,
  GraduationCap,
  LineChart,
  Lock,
  MessageCircle,
  Plug,
  ShieldCheck,
  TrendingUp,
  Trophy,
  Users,
  Wifi,
  Workflow,
} from "lucide-react";
import { KENTRO } from "@/lib/constants";

const SITE = "https://kentro.com.br";
const PAGE_URL = `${SITE}/sobre`;
const TITLE = "Sobre a Kentro — história, missão e 5 anos de operação (2021–2026)";
const DESC =
  "Conheça a Kentro: fundada em agosto de 2021 em Montes Claros (MG), durante a pandemia, hoje atende +1.600 empresas e +14.670 agentes com plataforma omnichannel, CRM e IA — API Oficial do WhatsApp (Meta) e conformidade com a LGPD.";

export const Route = createFileRoute("/sobre")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Início", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Sobre a Kentro", item: PAGE_URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: TITLE,
          description: DESC,
          url: PAGE_URL,
          mainEntity: {
            "@type": "Organization",
            name: "Kentro",
            legalName: "Barbalho Solutions Ltda.",
            url: SITE,
            logo: `${SITE}/favicon.svg`,
            description: DESC,
            foundingDate: "2021-08",
            foundingLocation: { "@type": "Place", name: "Montes Claros, MG, Brasil" },
            address: { "@type": "PostalAddress", streetAddress: KENTRO.address },
            email: KENTRO.email,
            founder: [
              { "@type": "Person", name: "Vitor Barbalho", jobTitle: "Founder & CEO" },
              { "@type": "Person", name: "Lucas Rocha", jobTitle: "Founder & CTO" },
            ],
            sameAs: [
              KENTRO.links.linkedin,
              KENTRO.links.instagram,
              KENTRO.links.youtube,
              KENTRO.links.tiktok,
            ],
          },
        }),
      },
    ],
  }),
  component: SobrePage,
});

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" as const },
};

function SectionTitle({
  eyebrow,
  title,
  intro,
}: {
  eyebrow?: string;
  title: string;
  intro?: string;
}) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      {eyebrow && (
        <span className="inline-flex items-center gap-2 rounded-full border border-primary-glow/30 bg-primary-glow/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-primary-glow">
          {eyebrow}
        </span>
      )}
      <h2 className="mt-4 text-3xl font-bold tracking-tight md:text-4xl">{title}</h2>
      {intro && <p className="mt-4 text-base text-muted-foreground md:text-lg">{intro}</p>}
    </div>
  );
}

function SobrePage() {
  return (
    <>
      {/* HERO INSTITUCIONAL */}
      <section className="relative overflow-hidden pt-24 pb-16 md:pt-28 md:pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary-glow/10" aria-hidden />
        <div className="circuit-pattern absolute inset-0 opacity-20" aria-hidden />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.span
            {...fadeUp}
            className="inline-flex items-center gap-2 rounded-full border border-primary-glow/30 bg-primary-glow/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-primary-glow"
          >
            Sobre a Kentro
          </motion.span>
          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-5 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl"
          >
            5 anos organizando o atendimento das{" "}
            <span className="gradient-text">empresas brasileiras</span>
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground"
          >
            Nascida em Montes Claros (MG) em agosto de 2021, a Kentro é a plataforma
            all-in-one de omnichannel, CRM e IA usada por +1.600 empresas e +14.670
            agentes todos os dias.
          </motion.p>
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-3"
          >
            <Link
              to="/contato"
              className="gradient-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-glow transition hover:opacity-95"
            >
              Agendar demonstração <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/equipe"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-6 py-3 text-sm font-medium text-muted-foreground transition hover:border-primary-glow/40 hover:text-foreground"
            >
              Conhecer o time e a cultura
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MISSÃO / POR QUE EXISTE */}
      <section className="py-20">
        <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:px-8">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              Por que a <span className="gradient-text">Kentro existe</span>
            </h2>
            <p className="mt-5 text-muted-foreground">
              O WhatsApp virou o coração das empresas — e também o maior buraco de vendas.
              Conversas espalhadas, clientes repetindo a história, leads sem origem e sem
              histórico. O atendimento é assíncrono (o cliente entra, sai e volta), mas a
              maioria opera como se fosse telefone.
            </p>
            <p className="mt-4 text-sm text-primary-glow">
              A Kentro nasceu para organizar isso — com dono de conversa, contexto e ritmo.
            </p>
          </motion.div>
          <motion.ul {...fadeUp} className="space-y-3">
            {[
              "Cliente manda \"oi\" às 9h, alguém responde 9h47 — e ele já comprou do concorrente.",
              "A história é repetida para 3 atendentes diferentes até o cliente desistir.",
              "Ninguém sabe de onde vêm os leads nem por que sumiram.",
            ].map((t, i) => (
              <li
                key={i}
                className="glass-card flex gap-3 rounded-2xl p-4 text-sm text-muted-foreground"
              >
                <MessageCircle className="mt-0.5 size-5 shrink-0 text-primary-glow" aria-hidden />
                <span>{t}</span>
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* HISTÓRIA + TIMELINE */}
      <section className="bg-secondary/30 py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-glow/30 bg-primary-glow/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-primary-glow">
              5 anos de Kentro — Agosto de 2026
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight md:text-4xl">
              Nossa <span className="gradient-text">história</span>
            </h2>
            <p className="mt-4 text-muted-foreground">
              A Kentro foi fundada em agosto de 2021, em Montes Claros (MG), durante a pandemia —
              quando o WhatsApp deixou de ser conveniência e virou sobrevivência para milhares de
              empresas. Começou resolvendo a bagunça do atendimento e evoluiu para uma plataforma
              all-in-one de omnichannel, CRM e IA. Em agosto de 2026, completamos 5 anos de
              operação contínua, crescendo junto com os nossos clientes.
            </p>
          </motion.div>

          <ol className="relative mt-12 grid gap-5 md:grid-cols-3 lg:grid-cols-6">
            <div
              aria-hidden
              className="absolute left-0 right-0 top-6 hidden h-px bg-gradient-to-r from-transparent via-primary-glow/40 to-transparent md:block"
            />
            {[
              { y: "2021", t: "Nascimento na pandemia, em Montes Claros (MG)." },
              { y: "2022", t: "Tração: faturamento acima de R$ 2 milhões." },
              { y: "2023", t: "+R$ 5 milhões (+150%) e Startup Destaque no InovAtiva Brasil." },
              { y: "2024", t: "Lançamento do Kentro ISP e da Kentro Academy." },
              { y: "2025", t: "API Oficial do WhatsApp (Meta), IA generativa e Kentro Podcast." },
              { y: "2026", t: "5 anos de Kentro: +1.600 empresas e +14.670 agentes na plataforma." },
            ].map((item, i) => (
              <motion.li
                key={item.y}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="glass-card relative flex flex-col rounded-2xl p-5"
              >
                <div className="gradient-primary inline-flex h-10 min-w-[4rem] items-center justify-center self-start whitespace-nowrap rounded-full px-3 text-sm font-bold leading-none text-white shadow-glow">
                  {item.y}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.t}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* AUTORIDADE: 5 ANOS, SEGURANÇA E CREDIBILIDADE */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary-glow/10" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SectionTitle
            eyebrow="Autoridade e confiança"
            title="5 anos cuidando dos dados dos nossos clientes"
            intro="Em um mercado cada vez mais guiado por IA, experiência comprovada é o que separa promessa de entrega. A Kentro opera desde 2021 com infraestrutura segura, responsabilidade com dados e credibilidade construída cliente a cliente."
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: ShieldCheck,
                title: "Infraestrutura segura",
                text: "Nuvem de alta disponibilidade, criptografia em trânsito e em repouso, e API Oficial do WhatsApp (Meta) — sem gambiarras, sem número clonado.",
              },
              {
                icon: Lock,
                title: "Responsabilidade com dados",
                text: "Tratamento de dados em conformidade com a LGPD, controle de acesso por perfil e trilhas de auditoria. Os dados da sua operação continuam seus.",
              },
              {
                icon: Award,
                title: "Credibilidade de mercado",
                text: "Startup Destaque no InovAtiva Brasil, presença nas principais feiras do setor e parceria oficial com a Meta para a API do WhatsApp.",
              },
              {
                icon: Building2,
                title: "Experiência comprovada",
                text: "5 anos de operação, +1.600 empresas atendidas e +14.670 agentes usando a Kentro todos os dias — de PMEs a operações nacionais.",
              },
            ].map((c, i) => (
              <motion.div
                key={c.title}
                {...fadeUp}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="glass-card rounded-2xl p-6"
              >
                <div className="gradient-primary inline-flex size-11 items-center justify-center rounded-xl text-white shadow-glow">
                  <c.icon className="size-5" />
                </div>
                <h3 className="mt-4 font-semibold">{c.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NÚMEROS */}
      <section className="bg-secondary/30 py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SectionTitle title="Números que mostram tração" />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {[
              { v: "5 anos", l: "de operação (2021–2026)", icon: Calendar },
              { v: "+1.600", l: "empresas atendidas", icon: Building2 },
              { v: "+14.670", l: "agentes usando a Kentro", icon: Users },
              { v: "+150%", l: "crescimento 2022 → 2023", icon: TrendingUp },
              { v: "Destaque", l: "InovAtiva Brasil 2023.2", icon: Trophy },
            ].map((s, i) => (
              <motion.div
                key={s.l}
                {...fadeUp}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="glass-card rounded-2xl p-6 text-center"
              >
                <s.icon className="mx-auto size-5 text-primary-glow" aria-hidden />
                <div className="gradient-text mt-2 text-3xl font-bold md:text-4xl">{s.v}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* A PLATAFORMA */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionTitle
            eyebrow="A plataforma"
            title="O que construímos"
            intro="Uma plataforma all-in-one para empresas brasileiras atenderem, venderem e crescerem."
          />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: MessageCircle,
                t: "Omnichannel",
                d: "WhatsApp (API Oficial Meta), Instagram, Facebook, Telegram, e-mail, voz, SMS e webchat em uma só tela.",
                href: "/omnichannel",
              },
              {
                icon: Users,
                t: "CRM e funil de vendas",
                d: "Leads, etapas, histórico e follow-ups que não caem no esquecimento.",
                href: "/gestao-de-atendimento",
              },
              {
                icon: Bot,
                t: "IA generativa",
                d: "Atendimento 24/7; resolve até 80% das dúvidas comuns e reduz até 70% da carga da equipe.",
                href: "/ia",
              },
              {
                icon: Workflow,
                t: "Automações",
                d: "Distribuição de conversas, disparos, agendamentos, APIs e webhooks.",
                href: "/integracoes",
              },
              {
                icon: LineChart,
                t: "BI e indicadores",
                d: "KPIs de atendimento, vendas e marketing em tempo real.",
                href: "/gestao-de-atendimento",
              },
              {
                icon: Plug,
                t: "Integrações",
                d: "REST API, webhooks e ERPs para conectar tudo que sua operação já usa.",
                href: "/integracoes",
              },
              {
                icon: GraduationCap,
                t: "Kentro Academy",
                d: "Treinamento e onboarding por nível: agente, gerência e diretoria.",
                href: KENTRO.links.academy,
                external: true,
              },
              {
                icon: Wifi,
                t: "Kentro ISP",
                d: "Vertical sob medida para provedores de internet.",
                href: "/segmentos/provedores-de-internet",
              },
            ].map((c, i) => {
              const Icon = c.icon;
              const inner = (
                <div className="glass-card group flex h-full flex-col rounded-2xl p-5 transition hover:-translate-y-1 hover:shadow-glow">
                  <span className="gradient-primary inline-flex size-11 items-center justify-center rounded-xl text-white shadow-glow">
                    <Icon className="size-5" />
                  </span>
                  <h3 className="mt-4 text-base font-semibold">{c.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{c.d}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-primary-glow opacity-0 transition group-hover:opacity-100">
                    Conhecer <ArrowRight className="size-3" />
                  </span>
                </div>
              );
              return (
                <motion.div
                  key={c.t}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                >
                  {c.external ? (
                    <a href={c.href} target="_blank" rel="noopener noreferrer" className="block h-full">
                      {inner}
                    </a>
                  ) : (
                    <Link to={c.href} className="block h-full">{inner}</Link>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* VISÃO DE FUTURO */}
      <section className="relative overflow-hidden bg-secondary/30 py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary-glow/10" aria-hidden />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.h2 {...fadeUp} className="text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
            Para onde <span className="gradient-text">vamos</span>
          </motion.h2>
          <motion.p {...fadeUp} className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
            Ser a referência brasileira em atendimento omnichannel com IA para PMEs — ajudando
            empresas a atender melhor, vender mais e crescer sem precisar aumentar a equipe na
            mesma proporção.
          </motion.p>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <SectionTitle
            title="Veja a Kentro funcionando na sua operação"
            intro="Demonstração ao vivo, sem compromisso — com os canais e o volume da sua empresa."
          />
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/contato"
              className="gradient-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-glow transition hover:opacity-95"
            >
              Agendar demonstração <ArrowRight className="size-4" />
            </Link>
            <a
              href={KENTRO.whatsappComercialUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-6 py-3 text-sm font-medium text-muted-foreground transition hover:border-primary-glow/40 hover:text-foreground"
            >
              <MessageCircle className="size-4" /> WhatsApp {KENTRO.whatsappDisplay}
            </a>
            <Link
              to="/equipe"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-6 py-3 text-sm font-medium text-muted-foreground transition hover:border-primary-glow/40 hover:text-foreground"
            >
              <Users className="size-4" /> Conhecer o time
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
